'use client';

import { useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useMemo, useState } from 'react';

import type { ProductHit } from '@libs/algolia';
import { getAlgoliaProductsByIds } from '@libs/api-proxy-client';
import { logSentryError } from '@libs/common-next';
import type { AccountProduct, Inventory } from '@libs/common-node';
import type { Nullable } from '@libs/core';
import { DeliveryType, getLocale } from '@libs/core';

import { STOCK_STATUS } from '@/components/account/OrderDetails/constants';
import type { OrderLine } from '@/components/account/OrderDetails/types';
import { useInventories } from '@/hooks/useInventories';
import type { ProductDeliveryMethods } from '@/types/OrderDetails';
import { FetchInventoryList } from '@/utils';
import {
    getProductDeliveryEligibility,
    mapProductHitToDeliveryMethods,
    personalizedProductMapper,
} from '@/utils/ordersUtils';

import { productQueryKey, useCachedProductData } from './useProductQuery';

export interface ProductInventoryContextProps {
    inventories?: Inventory[];
    isLoading: boolean;
    fetchInventories: (orderLine?: OrderLine[]) => Promise<void>;
    getAlgoliaProducts: (orderLine?: OrderLine[]) => Promise<void>;
    fetchProductsAndInventories: (orderLine?: OrderLine[]) => Promise<void>;
    getProductQuantity: (product: AccountProduct, orderedQuantity?: string, fulfillmentMethod?: string) => number;
    isProductOrderable: (orderLine: OrderLine) => boolean;
    fetchInventoriesBySkuStorePairs: (skuStorePairs: FetchInventoryList) => Promise<void>;
    getCachedProductData: (sku: string) => import('@libs/algolia').ProductHit | null;
}

export const ProductInventoryContext = createContext<Nullable<ProductInventoryContextProps>>(null);

export interface ProductInventoryProviderProps {
    children: ReactNode;
    initialValues?: {
        inventories?: Inventory[];
        productDeliveryMethodsList?: ProductDeliveryMethods[];
    };
}

export function useProductInventory(): ProductInventoryContextProps {
    const context = useContext(ProductInventoryContext);

    if (context === undefined || context === null) {
        throw new Error('useProductInventory must be used within an InventoryProvider');
    }

    return context;
}

export function ProductInventoryProvider({ children, initialValues }: Readonly<ProductInventoryProviderProps>) {
    const [productDeliveryMethodsList, setProductDeliveryMethodsList] = useState<Nullable<ProductDeliveryMethods[]>>(
        initialValues?.productDeliveryMethodsList
    );

    const queryClient = useQueryClient();
    const getCachedProductDataFromQuery = useCachedProductData();

    const {
        inventories,
        fetchInventories,
        getProductQuantity,
        isLoading,
        setIsLoading,
        fetchInventoriesBySkuStorePairs,
    } = useInventories(initialValues?.inventories);

    const getAlgoliaProducts = async (orderLines?: OrderLine[]) => {
        if (!orderLines) {
            return;
        }

        try {
            // Extract SKUs - use itemId for fetching, but also track product.sku for matching
            const skus = orderLines.map(orderLine => orderLine.itemId);
            const uniqueSkus = [...new Set(skus)];
            const skuMapping = new Map<string, string>(); // itemId -> product.sku mapping

            orderLines.forEach(orderLine => {
                if (orderLine.product?.sku) {
                    skuMapping.set(orderLine.itemId, orderLine.product.sku);
                }
            });

            // Check TanStack Query cache first - only fetch missing SKUs
            const missingSkus: string[] = [];
            const cachedProductHits = new Map<string, ProductHit>();

            uniqueSkus.forEach(itemId => {
                const cached = queryClient.getQueryData<ProductHit>(productQueryKey(itemId));
                if (cached) {
                    // TanStack Query handles cache expiration automatically via staleTime
                    cachedProductHits.set(itemId, cached);
                } else {
                    missingSkus.push(itemId);
                }
            });

            // If all SKUs are cached, skip fetching and just map the cached data
            if (missingSkus.length === 0 && cachedProductHits.size > 0) {
                const deliveryMethods = Array.from(cachedProductHits.values()).map(mapProductHitToDeliveryMethods);
                setProductDeliveryMethodsList(deliveryMethods);
                return;
            }

            setIsLoading(true);

            // Fetch only missing products using server-side cached batch endpoint from DP-2245
            const allProductHits = new Map<string, ProductHit>(cachedProductHits);

            if (missingSkus.length > 0) {
                try {
                    const locale = getLocale();
                    const productHits = await getAlgoliaProductsByIds({
                        ids: missingSkus,
                        locale,
                        caller: 'web:account:productInventory-client',
                    });

                    // Store fetched products in TanStack Query cache for future use
                    // Match each productHit to its corresponding itemId
                    productHits.forEach(productHit => {
                        if (productHit.objectID) {
                            // Try to find matching itemId by objectID or id
                            const itemId =
                                uniqueSkus.find(id => id === productHit.objectID || id === String(productHit.id)) ??
                                productHit.objectID;

                            queryClient.setQueryData(productQueryKey(itemId), productHit);
                            allProductHits.set(itemId, productHit);
                        }
                    });
                } catch (err) {
                    // Log to Sentry for critical errors
                    if (
                        err instanceof Error &&
                        'statusCode' in err &&
                        (err as { statusCode: number }).statusCode >= 500
                    ) {
                        logSentryError(err, {
                            tags: {
                                action: 'ProductCacheFetchFailed',
                                component: 'ProductInventoryProvider',
                                page: 'OrderDetails',
                                severity: 'HIGH',
                            },
                            extra: {
                                missingSkus: missingSkus.join(','),
                                message: 'Failed to fetch products from server-side cached endpoint',
                            },
                        });
                    }
                }
            }

            // Transform ProductHit[] to ProductDeliveryMethods[]
            const deliveryMethods = Array.from(allProductHits.values()).map(mapProductHitToDeliveryMethods);

            setProductDeliveryMethodsList(deliveryMethods);
        } catch (err) {
            logSentryError(err as Error, {
                tags: {
                    action: 'GetAlgoliaProductsFailed',
                    component: 'ProductInventoryProvider',
                    page: 'OrderDetails',
                    severity: 'HIGH',
                },
                extra: {
                    message: 'Critical error in getAlgoliaProducts',
                },
            });
        } finally {
            setIsLoading(false);
        }
    };

    const getCachedProductData = useMemo(() => getCachedProductDataFromQuery, [getCachedProductDataFromQuery]);

    const fetchProductsAndInventories = async (orderlines?: OrderLine[]) => {
        await fetchInventories(orderlines);
        await getAlgoliaProducts(orderlines);
    };

    // check if the product can be added to the cart
    const isProductOrderable = (orderLine: OrderLine) => {
        const { fulfillmentMethod, product, orderedQuantity, itemId } = orderLine;

        if (
            orderLine.stockStatus !== STOCK_STATUS.addToCartSTH &&
            orderLine.stockStatus !== STOCK_STATUS.addToCartInStore
        ) {
            return false;
        }

        const inStockQty = getProductQuantity(product, orderedQuantity, fulfillmentMethod);

        if (inStockQty === 0) {
            return false;
        }

        // Try to find by product.sku first, then fallback to itemId
        const productDeliveryMethods = productDeliveryMethodsList?.find(
            deliveryMethods => deliveryMethods.sku === product.sku || deliveryMethods.sku === itemId
        );

        const productDeliveryEligibility = getProductDeliveryEligibility(productDeliveryMethods);

        const isPersonalized = productDeliveryMethods
            ? personalizedProductMapper(productDeliveryMethods).isPersonalized
            : false;

        switch (fulfillmentMethod) {
            case DeliveryType.BOPIS:
                return isPersonalized ? false : !productDeliveryEligibility.bopisDisabled;
            case DeliveryType.STH:
                return productDeliveryEligibility.isShipToHomeSupported;
            case DeliveryType.SDD:
                return isPersonalized ? false : !productDeliveryEligibility.isSddDisabled;
            case DeliveryType.AUTOSHIP:
                return isPersonalized ? false : productDeliveryEligibility.isAutoshipSupported;
            default:
                return false;
        }
    };

    const contextValue = useMemo(
        () => ({
            inventories,
            isLoading,
            fetchInventories,
            getProductQuantity,
            getAlgoliaProducts,
            isProductOrderable,
            fetchProductsAndInventories,
            fetchInventoriesBySkuStorePairs,
            getCachedProductData,
        }),
        [
            inventories,
            isLoading,
            fetchInventories,
            getProductQuantity,
            getAlgoliaProducts,
            isProductOrderable,
            fetchProductsAndInventories,
            fetchInventoriesBySkuStorePairs,
            getCachedProductData,
            productDeliveryMethodsList,
        ]
    );

    return <ProductInventoryContext.Provider value={contextValue}>{children}</ProductInventoryContext.Provider>;
}
